import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { IUserSessionData, UserType } from 'app/store/models/user.model';
import { Button, ButtonColor, ButtonSize } from 'app/store/models/button.model';
import { AccountService } from 'app/store/services/account.service';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { SessionService } from '../../store/services/session.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { Store } from '@ngrx/store';
import { IReducers } from '../../store/reducers';
import { openSnackbar } from '../../store/actions/page.action';
import { ButtonDisablerService } from '../../store/services/button-disabler.service';
import { UtilitiesService } from '../../store/services/utilities.servive';
import { MatRadioChange } from '@angular/material/radio';

@Component({
  selector: 'app-create-account',
  templateUrl: './create-account.component.html',
  styleUrls: ['./create-account.component.scss'],
})
export class CreateAccountComponent implements OnInit, OnDestroy {
  userSession: IUserSessionData;

  formGroup: FormGroup;

  emailTakenError: boolean;

  createButton: Button = {
    color: ButtonColor.BLUE,
    text: 'Create',
    size: ButtonSize.SMALL,
    onClick: () => this.submitAccount(),
    style: 'outline',
  };

  componentReady = false;

  private destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(
    private accountService: AccountService,
    private router: Router,
    private readonly sessionService: SessionService,
    private readonly store$: Store<IReducers>,
    private readonly buttonDisablerService: ButtonDisablerService,
    private readonly utilService: UtilitiesService,
  ) {
  }

  get email() {
    return this.formGroup.get('email');
  }

  get firstName() {
    return this.formGroup.get('first_name');
  }

  get last_name() {
    return this.formGroup.get('last_name');
  }

  get company() {
    return this.formGroup.get('company');
  }

  get user_type() {
    return this.formGroup.get('user_type');
  }

  get country(): AbstractControl {
    return this.formGroup.get('country');
  }

  get password(): AbstractControl {
    return this.formGroup.get('password');
  }

  onUserTypeChange(event: MatRadioChange) {
    if ([UserType.MASTER.toString(), UserType.PARTNER.toString(), UserType.VSM.toString()].includes(event.value)) {
      this.password.setValidators([Validators.required, Validators.minLength(6)]);
      this.password.updateValueAndValidity();
      return
    }

    this.password.clearValidators();
    this.password.setValue('');
    this.password.updateValueAndValidity();
  }

  submitAccount() {
    if (this.formGroup.valid) {

      const data = this.formGroup.value;
      if (data.user_type !== UserType.MASTER && data.user_type !== UserType.PARTNER && data.user_type !== UserType.VSM) {
        delete data.password;
      }
      this.accountService
        .createAccount(data)
        .subscribe(
          () => {
            this.store$.dispatch(openSnackbar({
              snackbarData: {
                data: {
                  message: `Account successfully created. ${this.formGroup.value.user_type === 'master' ? '' : 'Instructions how to login and change the password are sent to user\'s email address'}`,
                  type: 'success',
                },
              },
            }));

            if (this.userSession.user_type === UserType.MANAGER) {
              this.router.navigate(['/accounts', 'view_representatives']);
              return;
            }
            this.router.navigate(['/accounts']);
          },
          error => this.handleError(error),
        );
    } else {
      this.formGroup.markAllAsTouched();
      this.buttonDisablerService.disableActive();
    }
  }

  handleError(error: HttpErrorResponse) {
    this.store$.dispatch(openSnackbar({
      snackbarData: {
        data: {
          message: error.error,
          type: 'error',
        },
      },
    }));
    if (error.error.code === 1002) {
      this.emailTakenError = true;
    }
  }

  ngOnInit() {
    this.sessionService
      .onReady()
      .pipe(takeUntil(this.destroy$))
      .subscribe((userSession: IUserSessionData) => {
        if (userSession) {
          this.userSession = userSession;

          this.formGroup = new FormGroup({
            email: new FormControl('', [Validators.required, Validators.email]),
            first_name: new FormControl('', [Validators.required]),
            last_name: new FormControl(''),
            company: new FormControl('', [Validators.required]),
            user_type: new FormControl(UserType.SINGLE_USER, [
              Validators.required,
            ]),
            country: new FormControl('', [Validators.required]),
            state: new FormControl(''),
            time_zone: new FormControl('', [Validators.required]),
            zip: new FormControl(''),
            address1: new FormControl(''),
            address2: new FormControl(''),
            phone_number: new FormControl(''),
            password: new FormControl(''),
            city: new FormControl(''),
          });
          this.componentReady = true;
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next(true);
  }

  getCountryItems() {
    return this.utilService.getCountryItems();
  }

  getStates() {
    const states = this.utilService.getStates()[this.formGroup.get('country').value];
    return Object.keys(states).map(k => ({ label: states[k], value: k }));
  }

  getTimezones() {
    const timezone = this.utilService.getTimezones(this.country.value);

    return timezone.map(k => ({ label: k[0], value: k[1] }));
  }
}
